import { createContext, useContext, useMemo, useState } from 'react'

import { ChevronRightIcon, EllipsisVertical, LinkIcon, RefreshCcw, SlidersHorizontal } from 'lucide-react';
import { Badge, Button, cn, DatePickerPreset, DefaultTooltip, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Separator } from 'twyne-new.ui';

import HeaderContainerGrid from '@/components/headerContainer/Grid'

const HeaderActions = ({ className, children }) => <>
  <div className={cn('justify-end items-center gap-4 flex', className)}>
    {children}
  </div>
</>

const HeaderSeparator = ({ className }) => <Separator orientation='vertical' className={cn('py-5 max-md:hidden', className)} />

const ReportConfigureContext = createContext(null)

const useReportConfigure = () => useContext(ReportConfigureContext)

const ReportHeader = ({ title, hideTitleOnMobile = false, children }) => {
  const [configureOpen, setConfigureOpen] = useState(false)
  const configure = useMemo(() => ({ open: configureOpen, setOpen: setConfigureOpen }), [configureOpen])

  return <ReportConfigureContext.Provider value={configure}>
    <HeaderContainerGrid
      title={title}
      className={cn(
        'max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:content-center max-md:h-auto max-md:min-h-[4.5rem] max-md:py-2 max-md:gap-x-2 max-md:gap-y-1',
        'max-md:[&>div:first-child]:min-w-0 max-md:[&_h1]:block max-md:[&_h1]:min-w-0 max-md:[&_h1]:truncate',
        hideTitleOnMobile && 'max-md:[&>div:first-child]:hidden',
      )}
    >
      <HeaderActions className='max-md:contents'>
        {children}
      </HeaderActions>
    </HeaderContainerGrid>
  </ReportConfigureContext.Provider>
}

const ReportHeaderDates = ({ fullHeight = false, children }) => <div className={cn(
  'md:contents flex flex-col items-start gap-1 max-md:col-start-1 max-md:[&>button]:w-auto max-md:[&>*]:self-start',
  fullHeight ? 'max-md:row-start-1 max-md:row-span-2' : 'max-md:row-start-2',
)}>
  {children}
</div>

const ReportHeaderDesktopActions = ({ children }) => <div className='hidden md:contents'>
  {children}
</div>

const ReportHeaderMobileActions = ({ children }) => <div className='md:contents flex items-center gap-2 max-md:col-start-2 max-md:row-start-1 max-md:row-span-2'>
  {children}
</div>

const ReportMobileMenu = ({ onOpenChange, children }) => <DropdownMenu onOpenChange={onOpenChange}>
  <DropdownMenuTrigger asChild>
    <Button variant='outline' className='md:hidden'>
      <EllipsisVertical size={16} />
    </Button>
  </DropdownMenuTrigger>
  <DropdownMenuContent align='end'>
    {children}
  </DropdownMenuContent>
</DropdownMenu>

const ReportMenuConfigure = () => {
  const { setOpen } = useReportConfigure()

  return <DropdownMenuItem icon={SlidersHorizontal} onSelect={() => setOpen(true)}>Configure</DropdownMenuItem>
}

const ReportMenuExpandable = ({ icon, label, open, onToggle, children }) => <>
  <DropdownMenuItem icon={icon} onSelect={event => {
    event.preventDefault()
    onToggle()
  }}>
    {label}
    <ChevronRightIcon size={16} className={cn('ml-auto transition-transform duration-300', open && 'rotate-90')} />
  </DropdownMenuItem>
  <div
    inert={open ? undefined : ''}
    className={cn('grid w-0 min-w-full transition-[grid-template-rows] duration-300', open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}
  >
    <div className='overflow-hidden'>
      {children}
    </div>
  </div>
</>

const RunButton = ({ onClick, isLoading, disabled }) => <Button disabled={disabled} size='lg' variant='outline' color="success" onClick={onClick}>
  <RefreshCcw size='14' className={isLoading ? 'animate-spin' : ''} />
</Button>

const ConfigureButton = ({ onClick = () => { } }) => <Button size="lg" variant='outline' onClick={onClick}>Configure</Button>

const ReportDatePicker = ({ dates, onChange, size = 'lg' }) => <DatePickerPreset
  value={{ $gte: dates?.startDate, $lte: dates?.endDate }}
  onChange={({ $gte: startDate, $lte: endDate }) => onChange({ startDate, endDate })}
  numberOfMonths={2}
  trigger={<TriggerDatePicker values={dates} size={size} />}
  valueType='moment'
/>

const PermanentLinkButton = ({ onClick = () => { } }) => <DefaultTooltip content='Permanent Link'>
  <Button size='lg' variant='outline' onClick={onClick}>
    <LinkIcon size='14' />
  </Button>
</DefaultTooltip>

const TriggerDatePicker = ({ values, size = 'lg' }) => <Badge
  size={size}
  color='accent'
  variant='light'
  className='self-center twy-subtle-accent-hover transition-all'
>
  {[values?.startDate?.format('MMM DD, YYYY'), 'to', values?.endDate?.format('MMM DD, YYYY') || '-'].join(' ')}
</Badge>

export { HeaderActions, HeaderSeparator, RunButton, ConfigureButton, ReportDatePicker, PermanentLinkButton, TriggerDatePicker, ReportHeader, ReportHeaderDates, ReportHeaderDesktopActions, ReportHeaderMobileActions, ReportMobileMenu, ReportMenuExpandable, ReportMenuConfigure, useReportConfigure }
